"use client";

import { useState, type ReactNode } from "react";
import type { BabyNameResponse } from "@/lib/api/baby-names";
import { SITE_URL } from "@/lib/schema";
import { NameCard } from "./NameCard";
import Button from "@ishvaram/ui/Button";

interface BabyNamesResultProps {
  result: BabyNameResponse;
  resultOffer: ReactNode;
  onReset: () => void;
}

export function BabyNamesResult({ result, resultOffer, onReset }: BabyNamesResultProps) {
  const [shortlist, setShortlist] = useState<Set<string>>(new Set());
  const [resultGender, setResultGender] = useState<string>("all");

  function toggleShortlist(name: string) {
    setShortlist((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  }

  function shareOnWhatsApp() {
    const shortlisted = result.names.filter((n) => shortlist.has(n.name));
    if (shortlisted.length === 0) return;

    const nakName = result.nakshatra?.name_en || "Unknown";
    const letters = result.starting_letters.join(", ");
    const namesList = shortlisted
      .map((n) => `${n.name} (${n.name_hi}) - ${n.meaning} [Score: ${n.compatibility_score}/10]`)
      .join("\n");

    const msg = encodeURIComponent(
      `Baby Name Shortlist - ${nakName} Nakshatra\n` +
        `Starting Letters: ${letters}\n` +
        `Mulank: ${result.mulank.value} | Bhagyank: ${result.bhagyank.value}\n\n` +
        `${namesList}\n\n` +
        `Find more names: ${SITE_URL}/baby-names`
    );
    window.open(`https://wa.me/?text=${msg}`, "_blank");
  }

  const filteredNames =
    resultGender === "all"
      ? result.names
      : result.names.filter((n) => n.gender === resultGender || n.gender === "unisex");

  return (
    <div className="space-y-6">
      <NakshatraInfo result={result} />
      {resultOffer}
      <ResultFilterBar
        resultGender={resultGender}
        resultCount={filteredNames.length}
        onGenderChange={setResultGender}
      />
      <ShortlistActions shortlistSize={shortlist.size} onShare={shareOnWhatsApp} />

      {/* Names List */}
      <div className="space-y-3">
        {filteredNames.map((entry) => (
          <NameCard
            key={entry.name + entry.nakshatra}
            entry={entry}
            selected={shortlist.has(entry.name)}
            onToggle={() => toggleShortlist(entry.name)}
          />
        ))}
      </div>

      {filteredNames.length === 0 && (
        <div className="text-center py-8">
          <p className="text-sm text-ish-text-muted">
            No names found for this combination. Try changing the gender filter or selecting all
            padas.
          </p>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Button
          variant="secondary"
          onPress={onReset}
          title="Search Again"
          className="text-center font-semibold py-3 px-6 rounded-lg text-[14px]"
        />
      </div>
    </div>
  );
}

function NakshatraInfo({ result }: { result: BabyNameResponse }) {
  return (
    <div className="card-sacred p-6 sm:p-8 text-center">
      <span className="text-4xl block mb-3">&#x2606;</span>
      <h2 className="text-2xl font-bold font-serif text-ish-dark capitalize">
        {result.nakshatra?.name_en?.replace(/_/g, " ") || "Selected Nakshatra"}
      </h2>
      {result.nakshatra?.name_hi && (
        <p className="text-base mt-1 text-ish-text-muted">{result.nakshatra.name_hi}</p>
      )}

      <div className="divider-warm my-5" />
      <StartingLetters result={result} />
      <NumerologyInfo result={result} />
    </div>
  );
}

function StartingLetters({ result }: { result: BabyNameResponse }) {
  return (
    <div className="mb-4">
      <span className="block text-[12px] mb-2 text-ish-text-faint">
        Recommended Starting Letters
      </span>
      <div className="flex justify-center gap-2 flex-wrap">
        {result.starting_letters.map((letter) => (
          <span
            key={letter}
            className={`px-3 py-1.5 rounded-lg text-[14px] font-bold border ${
              result.pada_letter === letter
                ? "bg-ish-accent text-white border-ish-accent"
                : "bg-ish-accent-light text-ish-accent border-ish-accent-border"
            }`}
          >
            {letter}
          </span>
        ))}
      </div>
      {result.pada_letter && (
        <p className="mt-2 text-[12px] text-ish-text-faint">
          Pada {result.pada} letter highlighted
        </p>
      )}
    </div>
  );
}

function NumerologyInfo({ result }: { result: BabyNameResponse }) {
  return (
    <div className="grid grid-cols-2 gap-4">
      <div>
        <span className="block text-[12px] mb-1 text-ish-text-faint">Mulank (Root)</span>
        <span className="font-bold text-[20px] text-ish-dark">{result.mulank.value}</span>
      </div>
      <div>
        <span className="block text-[12px] mb-1 text-ish-text-faint">Bhagyank (Destiny)</span>
        <span className="font-bold text-[20px] text-ish-dark">{result.bhagyank.value}</span>
      </div>
    </div>
  );
}

function ResultFilterBar({
  resultGender,
  resultCount,
  onGenderChange,
}: {
  resultGender: string;
  resultCount: number;
  onGenderChange: (value: string) => void;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex gap-1.5">
        {[
          { value: "all", label: "All" },
          { value: "male", label: "Boy" },
          { value: "female", label: "Girl" },
        ].map((opt) => (
          <button
            key={opt.value}
            className={`px-3 py-1.5 rounded-lg text-[12px] font-semibold transition-all border ${
              resultGender === opt.value
                ? "bg-ish-accent text-white border-ish-accent"
                : "bg-white text-ish-text-muted border-ish-border"
            }`}
            onClick={() => onGenderChange(opt.value)}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <span className="text-[12px] text-ish-text-faint">{resultCount} names</span>
    </div>
  );
}

function ShortlistActions({
  shortlistSize,
  onShare,
}: {
  shortlistSize: number;
  onShare: () => void;
}) {
  if (shortlistSize === 0) return null;
  return (
    <div className="card-sacred p-4 flex items-center justify-between border-[1.5px] border-ish-accent-border">
      <span className="text-[13px] font-semibold text-ish-dark">
        {shortlistSize} name{shortlistSize !== 1 ? "s" : ""} shortlisted
      </span>
      <button
        onClick={onShare}
        className="flex items-center gap-2 px-4 py-2 rounded-lg text-[13px] font-semibold transition-all bg-color-whatsapp text-white"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
        Share with Family
      </button>
    </div>
  );
}
